/** Reaktions-Chips (Zähler, eigene hervorgehoben, Tooltip „wer hat reagiert“) inkl. Super-Reaktionen. */
import { reactionKey, type MessageDto, type ReactionDto } from '@lagerfeuer/shared';
import { useQuery } from '@tanstack/react-query';
import { SmilePlus, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { EmojiGlyph, EmojiPickerPopover, type PickedEmoji } from '../../components/EmojiPicker';
import { anchorFrom, type Anchor } from '../../components/Popover';
import { Tooltip } from '../../components/Tooltip';
import { messagesApi } from '../../lib/endpoints';
import { qk } from '../../lib/queryClient';
import { useShallow } from 'zustand/react/shallow';
import { useAppStore } from '../../store/app';
import { displayName } from '../../store/selectors';
import { toast } from '../../store/ui';
import { useEmojiUrl } from './emojiCache';
import { describeSendError } from './send';

export interface ReactionPerms {
  canReact: boolean;
  canSuper: boolean;
  allowExternal: boolean;
}

/** Reaktion setzen/entfernen; Fehler als Toast. */
export async function toggleReaction(channelId: string, messageId: string, key: string, active: boolean, isSuper = false): Promise<void> {
  try {
    if (active) await messagesApi.unreact(channelId, messageId, key, isSuper);
    else await messagesApi.react(channelId, messageId, key, isSuper);
  } catch (e) {
    toast.error(isSuper ? 'Super-Reaktion fehlgeschlagen' : 'Reaktion fehlgeschlagen', describeSendError(e));
  }
}

export function addReaction(channelId: string, messageId: string, e: PickedEmoji, isSuper = false): Promise<void> {
  return toggleReaction(channelId, messageId, reactionKey(e), false, isSuper);
}

/** Emoji einer Reaktion (eigene Emojis werden bei Bedarf nachgeladen). */
export function ReactionEmoji({ emoji, size = 18 }: { emoji: ReactionDto['emoji']; size?: number }) {
  const url = useEmojiUrl(emoji.id);
  return <EmojiGlyph emoji={{ id: emoji.id, name: emoji.name, animated: emoji.animated, url }} size={size} />;
}

function emojiLabel(r: ReactionDto): string {
  return r.emoji.id ? `:${r.emoji.name}:` : r.emoji.name;
}

function ReactionChip({ message, r, isSuper, perms }: { message: MessageDto; r: ReactionDto; isSuper: boolean; perms: ReactionPerms }) {
  const [hovered, setHovered] = useState(false);
  const count = isSuper ? r.superCount : r.count;
  const active = isSuper ? r.meSuper : r.me;
  const q = useQuery({
    queryKey: [...qk.reactors(message.id, r.key), isSuper],
    queryFn: () => messagesApi.reactors(message.channelId, message.id, r.key, { limit: 10, super: isSuper }),
    enabled: hovered,
    staleTime: 30_000,
  });
  // Nur die Namen der angezeigten Nutzer abonnieren (nicht die ganze Nutzerliste je Chip).
  const names = useAppStore(useShallow((s) => (q.data ?? []).map((u) => displayName(s.users[u.id] ?? u))));
  let label = `${emojiLabel(r)} – ${count} ${isSuper ? (count === 1 ? 'Super-Reaktion' : 'Super-Reaktionen') : count === 1 ? 'Reaktion' : 'Reaktionen'}`;
  if (names.length) {
    const rest = count - names.length;
    const who = rest > 0 ? `${names.join(', ')} und ${rest} weitere` : names.length > 1 ? `${names.slice(0, -1).join(', ')} und ${names.at(-1)}` : names[0];
    label = `${who} ${names.length + Math.max(0, rest) === 1 ? 'hat' : 'haben'} mit ${emojiLabel(r)} ${isSuper ? 'super-' : ''}reagiert`;
  }
  const disabled = !active && (isSuper ? !perms.canSuper : false);
  return (
    <Tooltip label={label}>
      <button
        type="button"
        className={`reaction ${active ? 'reaction-me' : ''} ${isSuper ? 'reaction-super' : ''}`}
        aria-pressed={active}
        aria-label={label}
        disabled={disabled}
        onMouseEnter={() => setHovered(true)}
        onFocus={() => setHovered(true)}
        onClick={() => void toggleReaction(message.channelId, message.id, r.key, active, isSuper)}
        data-testid={isSuper ? 'reaction-super' : 'reaction'}
        data-key={r.key}
        data-me={active ? 'true' : 'false'}
      >
        {isSuper && <Sparkles size={12} className="reaction-sparkle" aria-hidden="true" />}
        <ReactionEmoji emoji={r.emoji} />
        <span className="reaction-count" data-testid="reaction-count">
          {count}
        </span>
      </button>
    </Tooltip>
  );
}

export function Reactions({ message, perms, serverId }: { message: MessageDto; perms: ReactionPerms; serverId: string | null }) {
  const [picker, setPicker] = useState<Anchor | null>(null);
  if (!message.reactions.length) return null;
  return (
    <div className="reactions" role="group" aria-label="Reaktionen" data-testid="reactions">
      {message.reactions.map((r) => (
        <span key={r.key} className="reaction-pair">
          {r.superCount > 0 && <ReactionChip message={message} r={r} isSuper perms={perms} />}
          {r.count > 0 && <ReactionChip message={message} r={r} isSuper={false} perms={perms} />}
        </span>
      ))}
      {perms.canReact && (
        <Tooltip label="Reaktion hinzufügen">
          <button type="button" className="reaction reaction-add" onClick={(e) => setPicker(anchorFrom(e.currentTarget))} data-testid="reaction-add">
            <SmilePlus size={16} />
          </button>
        </Tooltip>
      )}
      {picker && (
        <EmojiPickerPopover
          anchor={picker}
          onClose={() => setPicker(null)}
          serverId={serverId}
          allowExternal={perms.allowExternal}
          placement="top-start"
          onSelect={(e) => {
            const existing = message.reactions.find((r) => r.key === reactionKey(e));
            void toggleReaction(message.channelId, message.id, reactionKey(e), !!existing?.me);
          }}
        />
      )}
    </div>
  );
}
